import axios from 'axios';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import toast from 'react-hot-toast';

// Set axios defaults
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
axios.defaults.baseURL = API_URL;
axios.defaults.withCredentials = true;

// Create Auth Context
const AuthContext = createContext();

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        recruiter: action.payload.user || action.payload.recruiter,
        token: action.payload.token,
        error: null
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        recruiter: null,
        token: null,
        error: action.payload
      };
    case 'LOAD_USER_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        recruiter: action.payload,
        error: null
      };
    case 'LOAD_USER_FAILURE':
    case 'LOGOUT':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        recruiter: null,
        token: null,
        error: null
      };
    case 'UPDATE_RECRUITER':
      return {
        ...state,
        recruiter: { ...state.recruiter, ...action.payload }
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Initial State
const initialState = {
  loading: !!localStorage.getItem('recruiter_token'),
  isAuthenticated: false,
  recruiter: null,
  token: localStorage.getItem('recruiter_token'),
  error: null
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user on app start
  const loadUser = async () => {
    const token = localStorage.getItem('recruiter_token');
    
    if (!token) {
      dispatch({ type: 'LOAD_USER_FAILURE' });
      return;
    }

    try {
      setAuthToken(token);
      
      console.log('🔄 RECRUITER FRONTEND - Loading user with token:', {
        tokenLength: token.length,
        endpoint: '/api/auth/me'
      });
      
      const res = await axios.get('/api/auth/me');
      
      console.log('✅ RECRUITER FRONTEND - User loaded successfully:', {
        user: res.data.user,
        isAuthenticated: true
      });
      
      dispatch({
        type: 'LOAD_USER_SUCCESS',
        payload: res.data.user
      });
    } catch (err) {
      console.error('❌ RECRUITER FRONTEND - Load user error:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      dispatch({ type: 'LOAD_USER_FAILURE' });
      localStorage.removeItem('recruiter_token');
      setAuthToken(null);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  // Register
  const register = async (formData) => {
    dispatch({ type: 'REGISTER_START' });
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      const res = await axios.post('/api/auth/register/recruiter', formData, config);

      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: res.data
      });

      localStorage.setItem('recruiter_token', res.data.token);
      setAuthToken(res.data.token);

      toast.success('Registration successful! Welcome to jobzentro!');
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      dispatch({
        type: 'REGISTER_FAILURE',
        payload: message
      });
      toast.error(message);
      return { success: false, message };
    }
  };

  // Login
  const login = async (email, password) => {
    console.log('🔐 RECRUITER FRONTEND - Login attempt started:', {
      email,
      password,
      passwordLength: password ? password.length : 0,
      userType: 'recruiter',
      apiUrl: '/api/auth/login',
      timestamp: new Date().toISOString()
    });

    dispatch({ type: 'LOGIN_START' });
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      const requestData = { email, password, userType: 'recruiter' };
      
      console.log('📤 RECRUITER FRONTEND - Sending request:', {
        url: '/api/auth/login',
        method: 'POST',
        headers: config.headers,
        data: requestData
      });

      const res = await axios.post('/api/auth/login', requestData, config);

      console.log('📥 RECRUITER FRONTEND - Response received:', {
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
        data: res.data
      });

      console.log('✅ RECRUITER FRONTEND - Login successful:', {
        user: res.data.user,
        tokenLength: res.data.token ? res.data.token.length : 0,
        success: res.data.success
      });

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: res.data
      });

      localStorage.setItem('recruiter_token', res.data.token);
      setAuthToken(res.data.token);

      toast.success('Welcome back!');
      return { success: true, data: res.data };
    } catch (err) {
      console.error('❌ RECRUITER FRONTEND - Login error:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
        stack: err.stack
      });

      const message = err.response?.data?.message || 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: message
      });
      toast.error(message);
      return { success: false, message };
    }
  };

  // Logout
  const logout = () => {
    dispatch({ type: 'LOGOUT' });
    localStorage.removeItem('recruiter_token');
    setAuthToken(null);
    toast.success('Logged out successfully');
  };

  // Update recruiter
  const updateRecruiter = async (recruiterData) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      const res = await axios.put('/api/recruiters/profile', recruiterData, config);

      dispatch({
        type: 'UPDATE_RECRUITER',
        payload: res.data
      });

      toast.success('Profile updated successfully');
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.message || 'Update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Upload logo
  const uploadLogo = async (file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      const res = await axios.post('/api/upload/recruiters/upload-logo', formData, config);

      dispatch({
        type: 'UPDATE_RECRUITER',
        payload: { logo: res.data.logoUrl }
      });

      toast.success('Logo uploaded successfully');
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.message || 'Upload failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      await axios.put('/api/recruiters/change-password', 
        { currentPassword, newPassword }, 
        config
      );

      toast.success('Password changed successfully');
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Clear errors
  const clearErrors = () => dispatch({ type: 'CLEAR_ERRORS' });

  // Set auth token
  const setAuthToken = (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Set initial token if it exists
  if (state.token) {
    setAuthToken(state.token);
  }

  const value = {
    ...state,
    register,
    login,
    logout,
    updateRecruiter,
    uploadLogo,
    changePassword,
    clearErrors,
    loadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 